// packages/client/src/components/project/miller/ContentColumn.tsx
import { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { Plus, FileText, AlertTriangle, Trash2, FolderInput } from 'lucide-react';
import MoveToFolderModal from './MoveToFolderModal';
import type { KnowledgeItem } from '@estimator/shared';
import { toast } from 'sonner';
import { useDrawingSets, useDeleteDrawingSet } from '@/api/drawing-sets';
import { useKnowledgeItems, useDeleteKnowledge } from '@/api/knowledge';
import { SetStatusBadge } from './StatusBadge';
import type { Folder, DrawingSet } from '@estimator/shared';

interface ContentColumnProps {
  projectId: string;
  folder: Folder | null;
  isUnfiled?: boolean;
  selectedSetId: string | null;
  onSelectSet: (set: DrawingSet) => void;
  onSelectFile: (knowledgeItemId: string) => void;
  onNewSet: () => void;
  onUpload: () => void;
}

export default function ContentColumn({
  projectId,
  folder,
  isUnfiled = false,
  selectedSetId,
  onSelectSet,
  onSelectFile,
  onNewSet,
  onUpload,
}: ContentColumnProps) {
  const isShopDrawings = folder?.folder_type === 'SHOP_DRAWINGS';
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string; type: 'file' | 'set' } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    item: KnowledgeItem;
  } | null>(null);
  const [moveTarget, setMoveTarget] = useState<KnowledgeItem | null>(null);

  // For shop drawings: fetch drawing sets
  const { data: setsResponse } = useDrawingSets(
    projectId,
    isShopDrawings ? folder?.id ?? null : null,
  );
  const sets = setsResponse?.data ?? [];

  // For other folders: fetch knowledge items with this folder_id
  const { data: itemsResponse } = useKnowledgeItems(projectId);
  const folderItems = (itemsResponse?.data ?? []).filter(
    (item) => isUnfiled ? item.folder_id == null : item.folder_id === folder?.id,
  );

  const handleContextMenu = (e: React.MouseEvent, item: KnowledgeItem) => {
    e.preventDefault();
    e.stopPropagation();
    const menuWidth = 160;
    const menuHeight = 80;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight - 8);
    setContextMenu({ x, y, item });
  };

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClose = () => setContextMenu(null);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    window.addEventListener('click', handleClose);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [contextMenu]);

  const deleteFileMutation = useDeleteKnowledge(projectId);
  const deleteSetMutation = useDeleteDrawingSet(projectId);

  const handleDelete = () => {
    if (!deleteTarget) return;
    const { title, type } = deleteTarget;
    const mutation = type === 'set' ? deleteSetMutation : deleteFileMutation;
    mutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`${title} deleted`);
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(`Failed to delete: ${err.message}`);
        setDeleteTarget(null);
      },
    });
  };

  if (!folder && !isUnfiled) return null;

  return (
    <div
      className="flex flex-col h-full border-r"
      style={{ borderColor: 'var(--border)', minWidth: 220 }}
    >
      <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {isUnfiled ? 'Unfiled' : folder!.name}
        </h3>
        {!isUnfiled && (
          <Button
            size="sm"
            variant="light"
            isIconOnly
            onPress={isShopDrawings ? onNewSet : onUpload}
            title={isShopDrawings ? 'New Set' : 'Upload'}
          >
            <Plus size={14} />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isShopDrawings && !isUnfiled ? (
          // Drawing sets view
          sets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No drawing sets yet</p>
            </div>
          ) : (
            sets.map((set) => (
              <div
                key={set.id}
                className="group flex items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer"
                style={{
                  backgroundColor: selectedSetId === set.id ? 'var(--bg-hover)' : 'transparent',
                  color: selectedSetId === set.id ? 'var(--accent)' : 'var(--text-primary)',
                }}
                onClick={() => onSelectSet(set)}
              >
                <span className="flex-1 truncate">{set.name}</span>
                <SetStatusBadge approved={set.approved_count ?? 0} total={set.sheet_count ?? 0} />
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-[var(--bg-hover)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget({ id: set.id, title: set.name, type: 'set' });
                  }}
                  title="Delete set"
                >
                  <Trash2 size={12} style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>
            ))
          )
        ) : (
          // Regular files view
          folderItems.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No files yet</p>
            </div>
          ) : (
            folderItems.map((item) => (
              <div
                key={item.id}
                className="group flex items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer"
                style={{ color: 'var(--text-primary)' }}
                onClick={() => onSelectFile(item.id)}
                onContextMenu={(e) => handleContextMenu(e, item as KnowledgeItem)}
              >
                {item.processing_status === 'processing' ? (
                  <Spinner size="sm" color="warning" classNames={{ wrapper: 'w-[14px] h-[14px] shrink-0' }} />
                ) : item.processing_status === 'failed' ? (
                  <AlertTriangle size={14} style={{ color: 'var(--error)', flexShrink: 0 }} />
                ) : (
                  <FileText size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                )}
                <span className="flex-1 truncate">{item.title}</span>
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-[var(--bg-hover)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget({ id: item.id, title: item.title, type: 'file' });
                  }}
                  title="Delete"
                >
                  <Trash2 size={12} style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>
            ))
          )
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 min-w-[160px] rounded-md border py-1 shadow-lg"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            backgroundColor: 'var(--bg-surface)',
            borderColor: 'var(--border)',
          }}
        >
          <button
            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--text-primary)' }}
            onClick={(e) => {
              e.stopPropagation();
              setMoveTarget(contextMenu.item);
              setContextMenu(null);
            }}
          >
            <FolderInput size={14} style={{ color: 'var(--text-muted)' }} />
            Move to…
          </button>
          <button
            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-[var(--bg-hover)]"
            style={{ color: 'var(--error)' }}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget({
                id: contextMenu.item.id,
                title: contextMenu.item.title,
                type: 'file',
              });
              setContextMenu(null);
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Move to folder modal */}
      {moveTarget && (
        <MoveToFolderModal
          isOpen={!!moveTarget}
          onClose={() => setMoveTarget(null)}
          projectId={projectId}
          item={moveTarget}
        />
      )}

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm">
        <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
          <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Delete {deleteTarget?.type === 'set' ? 'Drawing Set' : 'File'}
          </ModalHeader>
          <ModalBody>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{deleteTarget?.title}</strong>? This cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="bordered"
              onPress={() => setDeleteTarget(null)}
              className="border-[var(--border)] text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="danger"
              onPress={handleDelete}
              isLoading={deleteFileMutation.isPending || deleteSetMutation.isPending}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
